import React, { useEffect, useMemo, useState, type FC } from 'react'
import { useIntl } from 'react-intl'
import { useAtom } from 'jotai'
import styled from '@emotion/styled'

import {
  Select,
  GridCell,
  Link,
  InputText,
  LINK_VARIANT_DEFAULT,
  FormElement,
} from 'mailup-web-components'

import { type ConditionProps } from '../../types'
import { filterFormImmerAtom } from '../../state/state'
import { type UiActivityMessageLinkSpec } from '../../filterConditionTypes'
import { type SelectOption } from '../../../../helpers/types'
import { getMessageLinksUrls } from '../../services/specServices'
import { activityDefaultMsgValues } from '../../utils/conditionOptions'
import ActivityMessageCondition from './ActivityMessageCondition'
import { validateLinkValue } from '../../utils/activitySpecValidation'

const StyledLink = styled(Link)`
  padding-top: 8px;
  position: absolute;
`

const ActivityLinkCondition: FC<ConditionProps> = ({
  blockIndex,
  condIndex,
}) => {
  const intl = useIntl()
  const [filter, setFilter] = useAtom(filterFormImmerAtom)
  const condition = filter.Blocks[blockIndex].Conditions[condIndex]
  const spec = condition.Spec as UiActivityMessageLinkSpec
  const feedback = !validateLinkValue(spec, filter)
    ? intl.formatMessage({ id: 'Load.Filters.InputErrorFilterMultilist' })
    : undefined

  const canSuggestLinks: boolean = useMemo(
    () =>
      spec.Message !== undefined &&
      !activityDefaultMsgValues.includes(spec.Message),
    [spec.Message],
  )
  const [isFreeText, setIsFreeText] = useState(true)
  const [linkOpts, setLinkOpts] = useState<SelectOption[]>([])
  const [loadingOpts, setLoadingOpts] = useState(false)

  useEffect(() => {
    const refreshLinkOpts = async (): Promise<void> => {
      setLoadingOpts(true)
      if (
        spec.Message !== undefined &&
        !activityDefaultMsgValues.includes(spec.Message)
      ) {
        const links = await getMessageLinksUrls(spec.Message)
        const mappedOpts =
          links?.map((l) => {
            return { value: l, label: l }
          }) ?? []
        setLinkOpts(mappedOpts)
      } else {
        setLinkOpts([])
      }
      setLoadingOpts(false)
    }
    void refreshLinkOpts()
  }, [intl, spec.Message])

  const handleValueStringChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const valueString = e.target.value
    handleInputChange(valueString)
  }

  const handleInputChange = (link: string): void => {
    setFilter((draft) => {
      const draftSpec = draft.Blocks[blockIndex].Conditions[condIndex]
        .Spec as UiActivityMessageLinkSpec
      draftSpec.Link = link
    })
  }

  return (
    <>
      <ActivityMessageCondition blockIndex={blockIndex} condIndex={condIndex} />
      <GridCell span={8}>
        <FormElement
          data-mu-test='filterForm-activity-link_element'
          feedback={feedback}
        >
          {(!canSuggestLinks || isFreeText) && (
            <InputText
              data-mu-test='filterForm-activity-link'
              value={spec.Link}
              onChange={handleValueStringChange}
              placeholder={intl.formatMessage({
                id: 'Load.Filters.ActivityLinkPlaceholder',
              })}
            />
          )}
          {canSuggestLinks && !isFreeText && (
            <Select
              data-mu-test='filterForm-activity-link'
              value={spec.Link}
              options={linkOpts}
              loading={loadingOpts}
              onChange={handleInputChange}
              placeholder={intl.formatMessage({
                id: 'Load.Filters.ActivityLinkPlaceholder',
              })}
            />
          )}
        </FormElement>
      </GridCell>
      <GridCell span={4}>
        {canSuggestLinks && !isFreeText && (
          <StyledLink
            variant={LINK_VARIANT_DEFAULT}
            onClick={() => {
              setIsFreeText(true)
            }}
          >
            {intl.formatMessage({ id: 'Load.Filters.ActivityLinkManual' })}
          </StyledLink>
        )}
        {canSuggestLinks && isFreeText && (
          <StyledLink
            variant={LINK_VARIANT_DEFAULT}
            onClick={() => {
              setIsFreeText(false)
            }}
          >
            {intl.formatMessage({ id: 'Load.Filters.ActivityLinkAuto' })}
          </StyledLink>
        )}
      </GridCell>
    </>
  )
}

export default ActivityLinkCondition
